// Portfolio (book of record) methodology copy.
//
// Written during the 2026-08-27 Portfolio rework and grounded in what was VERIFIED in the
// code while doing it — not in what the page appears to claim. Where a figure is a mark
// rather than an executable price, a floor rather than a count, or a check that cannot
// fail, this file says so. Keep claims consistent with the code if you edit copy here.
//
// Source of truth for the numbers described: strategy/portfolio_view.py,
// latest_nav/__init__.py, strategy/home_run_redeployment.py, and PortfolioView.tsx.

import type { MethodologyContent } from "./methodologyTypes";

export const PORTFOLIO_METHODOLOGY: MethodologyContent = {
  intro: {
    eyebrow: "Portfolio Reference",
    title: "Methodology",
    subtitle:
      "How every figure on the Portfolio page is computed, which denominator it uses, and what it deliberately does not tell you.",
    purpose:
      "Portfolio is the fund's book of record: what we own, what it cost, and proof the parts add up to NAV. It is not the sizing page — Signals owns target-vs-current and the deployment cap. It is not the risk page — Risk owns stress, greek exposure and the administrator reconciliation in depth. It is not the ticket queue — the Trade Desk owns recording what you actually traded — and it is NOT where a harvest is recorded: the Record TWS Trim control and the redeployment lockout moved to Signals on 2026-08-27 (CIO decision Q7). This page is READ-ONLY: every figure here is a book-of-record read, and the harvest row in the audit strip names Signals as where it is handled.",
    coreRule:
      "Two denominators are in play and they are NOT interchangeable. The NAV hero divides by the RECONCILED total (IBKR broker book + off-broker bank cash + accruals). Every sizing figure divides by the BROKER BOOK, which is smaller — so the LEAPS share in the hero always reads lower than the LEAPS share in the sizing drawer, for the same sleeve at the same instant. Both are correct; each is labelled where it appears. Never quote one against the other's target.",
  },

  toneLegends: [
    {
      group: "Owed action today",
      items: [
        {
          label: "Open rows",
          meaning:
            "Something is asking for you today. Each row names WHERE it is handled — this page cannot action most of what it flags.",
        },
        {
          label: "All clear",
          meaning:
            "Nothing is owed. The panel still lists what it checked, so a quiet day reads as 'checked and clear' rather than 'failed to load'.",
        },
        {
          label: "Counts only",
          meaning:
            "These rows never carry a dollar amount or a percentage, on purpose. The moment a figure appears here it becomes a second place that figure lives.",
        },
      ],
    },
    {
      group: "Audit checks",
      items: [
        {
          label: "Pass",
          meaning: "The check ran and found nothing wrong.",
        },
        {
          label: "Unknown / not run",
          meaning:
            "Rendered in WARNING tone, never neutral and never green. 'We could not check' is a finding, not a shrug — and for the administrator cross-check, absence of the data IS the failure signal.",
        },
        {
          label: "Attention",
          meaning:
            "A check failed. The figures above it may still be displayed, but they should not be quoted until it is resolved.",
        },
      ],
    },
    {
      group: "Sizing verdicts",
      items: [
        {
          label: "Trim",
          meaning:
            "The rebalance engine staged a trim to target this run: a confirmed defense in any band (including the 40% Sweet-Spot target) or the grind axis. A regime target drop alone is not a trim until the sell gate confirms. The only state that makes a trim executable.",
        },
        {
          label: "At cap",
          meaning:
            "Funded/cost basis is at or above the 40% deployment cap. This means STOP BUYING. It is not a trim signal.",
        },
        {
          label: "No trim required",
          meaning:
            "Market value drifted above target on winners while funded basis stays under the cap. Expected, and benign.",
        },
      ],
    },
  ],

  panels: [
    {
      name: "Total NAV (now) and composition",
      summary:
        "The headline number and proof that its parts add up. This is the page's whole reason to exist.",
      metrics: [
        {
          key: "USD",
          label: "Total NAV (now)",
          why: "The fund's current estimated value, reconciled rather than broker-only.",
          how: "reconciliation.daily.estimated_nav_now from /risk/nav_latest.",
          subject: "reconciliation",
          timeBasis: "live",
          role: "official",
          whatItIs:
            "IBKR broker book plus off-broker bank cash plus accruals, as estimated between administrator strikes.",
          tellsYou: "What the fund is worth right now, on the same basis the Risk and Signals cockpits use.",
          movesIt:
            "Position marks, IBKR cash, the bank-cash and accrual overlay carried from the last official strike.",
          readIt:
            "If the audit strip says 'NAV basis: Broker only', this is NOT reconciled — the administrator overlay failed to load and the figure silently fell back to the IBKR book alone.",
          limits:
            "Estimated and unofficial between strikes. The official NAV is struck monthly by NAV Fund Services; this is not it. " +
            // CIO-approved disclosure, 2026-08-31. Until this date GET /risk/nav_latest
            // defaulted to persisting, so any dashboard page load rewrote the day's stored
            // NAV row: 116 of 769 rows were rewritten after creation and the stored value
            // for a day depended on when a tab was last open. Not restated -- the originals
            // were never retained. Kept short because this dashboard has one viewer; the
            // full record lives in the repo docs.
            "Daily NAV rows recorded between 19 March 2026 and 31 August 2026 were captured at inconsistent times of day and may not represent an end-of-session mark; they have not been restated. From 31 August 2026 the daily row is written only by scheduled jobs, the last at 13:45 MT. Internal operating figures only — this does not affect the Administrator's official Net ROR.",
          readWith: "The audit checks directly below, and the period return beside it.",
          source: "/risk/nav_latest, falling back to /get_portfolio_nav_latest (which carries NO reconciliation block).",
        },
        {
          key: "%",
          label: "NAV chart — Return (per unit) vs NAV $ (level)",
          why: "A NAV path rises for two different reasons, and only one of them is performance.",
          how:
            "Backend /risk/nav_intraday?range=… returns both the NAV level series and a per-unit INDEX: chained session by session, dividing each step by (prior mark + any capital flow effective on or before that point). That is the same construction the administrator uses for NAV per share, so a subscription raises the fund's level and the unit count together and cancels out of the line. Official month-end NAV-per-share strikes are fetched from NAV Fund Services, rebased on the window's own base, and drawn as hollow anchor dots.",
          subject: "reconciliation",
          timeBasis: "live",
          role: "informational",
          whatItIs:
            "Two views of the same book. Return (the default) is per-unit performance with capital flows divided out. NAV $ is the dollar level INCLUDING flows — real, and useful for fund size, but a subscription reads as a step up in it.",
          tellsYou:
            "Return answers 'how did the strategy do'. NAV $ answers 'how big is the fund'. Before 3 September 2026 only the level line existed, so a $576K admission drew a +3.5% cliff that was not performance.",
          movesIt:
            "Position marks and IBKR cash move both. Subscriptions, redemptions and fee wires move only the level.",
          readIt:
            "Check which mode you are in — the active button is filled, and the Δ chip names its own basis ('per unit' or 'level'). The figure you are NOT looking at is printed beside it, and the two differ by exactly the window's capital flows. Today's point is the freshest live intraday mark (labelled 'today live'), not a daily mark of record, which is only promoted after the close.",
          limits:
            "ESTIMATED AND UNOFFICIAL, and BROKER-DERIVED: the line is gross of the management fee and the accrued incentive that the administrator's official Net ROR is net of, so it sits ABOVE the official figure in profitable periods — the gap to the anchor dots is fees and carry, not tracking error. " +
            "KNOWN TRANSIENT, and it is not one-directional: an admission is effective the 1st but the Axos→IBKR sweep can land a day or two later, so the effective date can read low and the sweep day can read high — chained, that prints as a fabricated up-day, which is why the chart says so whenever a subscription is in the window. Treat the sessions around ▲ as settling. " +
            "The per-unit index needs a base mark; where none exists the chart falls back to the level line and the mode toggle disappears. Anchors are omitted entirely unless an official strike sits within 10 days of the window's base — a curve on the wrong base would read as a break with the administrator. " +
            "Note the three flow-adjusted constructions on this platform are deliberately different and agree only at short horizons: the 1D chart subtracts flows once, the range chart chains per-unit, and the Est.-return tile subtracts over one window from the official strike.",
          readWith: "The Est. return tile beside it, which is net of the modelled fee and carries the modelled after-incentive figure.",
          source: "/risk/nav_intraday (range mode); NAVPS anchors from NAV Fund Services FundAccountingData, TTL-cached and fail-soft.",
        },
        {
          key: "% OF NAV (RECONCILED)",
          label: "Composition rows",
          why: "What the NAV is made of, as shares that add back to it.",
          how: "Each sleeve's market value divided by the reconciled total above.",
          subject: "book",
          timeBasis: "live",
          role: "informational",
          whatItIs: "LEAPS, fixed income, IBKR cash & residual, off-broker bank cash, accruals.",
          tellsYou: "Where the money currently sits.",
          readIt:
            "The LEAPS share here is systematically LOWER than the LEAPS share in the sizing drawer, because this divides by the reconciled total and that divides by the broker book. This is not a discrepancy.",
          limits:
            "'IBKR cash & residual' is defined as ibkr_live_nav minus the LEAPS and FI marks, so it ABSORBS any mark/snapshot timing difference on the broker side. That is why the tie-out below can never break on the broker leg.",
          readWith: "The tie-out line and the 'Other / unmodeled' row.",
          source: "/get_portfolio_view holdings, /risk/nav_latest overlay legs.",
        },
        {
          key: "USD",
          label: "Other / unmodeled (plug row)",
          why: "An unexplained component of NAV.",
          how: "Rendered only when |headline NAV − sum of components| exceeds max($1,000, 0.2% of NAV).",
          subject: "reconciliation",
          timeBasis: "live",
          role: "informational",
          whatItIs: "The residual the itemised components do not account for.",
          tellsYou: "That something in NAV is not modelled by the rows above it.",
          limits:
            "ABSENCE OF THIS ROW IS NOT A TIE-OUT. It only appears above a threshold, and the broker leg self-cancels by construction (see the composition note), so only the off-broker bank/accrual legs can produce it — and only when the administrator feed is loaded. This is why the audit strip carries no 'NAV ties out' tick: it would be a check that cannot fail.",
          readWith: "Audit checks → NAV basis, and → Off-broker overlay.",
          source: "Computed on the page from the payloads above.",
        },
      ],
    },
    {
      name: "Audit checks",
      summary:
        "Can the number above be trusted? States only — no dollars, no percentages, so an audit check can never be misread as a fund figure.",
      metrics: [
        {
          key: "STATE",
          label: "NAV basis",
          why: "Whether the administrator overlay was applied at all.",
          how: "Reads whether latestNav.reconciliation is present.",
          subject: "reconciliation",
          timeBasis: "live",
          role: "official",
          whatItIs: "'Reconciled' means the overlay applied; 'Broker only' means it did not.",
          tellsYou:
            "The single highest-leverage line on the page. The NAV loader silently falls back to a legacy endpoint that carries no reconciliation block, so the page can revert to the broker book while a perfectly plausible NAV keeps rendering.",
          limits: "When this reads 'Broker only', the two rows below it are meaningless and read 'Unknown'.",
          source: "/risk/nav_latest.",
        },
        {
          key: "STATE",
          label: "Administrator cross-check",
          why: "Whether NAV Fund Services agrees with the broker NAV.",
          how: "Four states read from reconciliation.administrator, mirroring the Risk cockpit exactly.",
          subject: "reconciliation",
          timeBasis: "snapshot",
          role: "official",
          whatItIs:
            "Reconciled / Pending close / Break / Not run, at the last official strike.",
          readIt:
            "'Pending close' is NOT a break — it means the bank/accruals split is withheld until the administrator closes. This is the state that produced a false 'Break' in July 2026.",
          limits:
            "'Not run' covers BOTH 'no cross-check yet' and 'the feed threw'. The backend swallows every exception and returns without the key, so the two are indistinguishable from here. Never read absence as agreement. This deliberately does not use the top-level reconciliation.break_flag, which is hardcoded false whenever component NAVs are absent — i.e. on the normal live path.",
          readWith: "Risk, which shows the same four states with the underlying figures.",
          source: "latest_nav/__init__.py administrator cross-check block.",
        },
        {
          key: "USD",
          label: "Off-broker overlay",
          why: "How far the headline sits above the IBKR broker book.",
          how: "reconciliation.daily.admin_overlay_usd.",
          subject: "reconciliation",
          timeBasis: "snapshot",
          role: "informational",
          whatItIs: "Official NAV minus broker NAV at the last monthly strike — bank cash plus accruals.",
          limits:
            "CARRIED, not measured today. It is only as current as the last strike. When the bank feed is untrusted the split is withheld and this is the combined overlay, marked '(est.)'.",
          source: "/risk/nav_latest reconciliation.daily.",
        },
        {
          key: "SESSIONS",
          label: "Greeks snapshot",
          why: "How stale the risk snapshot behind the marks is.",
          how: "The OLDEST non-null risk_updated_at_utc across held lines, in trading sessions.",
          subject: "book",
          timeBasis: "snapshot",
          role: "situational",
          whatItIs: "The age of the least-fresh line, not the freshest.",
          readIt:
            "Deliberately the MINIMUM. The headline freshness chip takes the max across rows, so one freshly-quoted line makes an otherwise stale book read 'current'.",
          limits:
            "risk_updated_at_utc is NULL by construction for any row whose risk_source is 'held_leaps' or 'missing' — so the least-fresh positions are exactly the ones a max() cannot see. Those are counted separately. Grace is 2 trading sessions.",
          source: "/get_portfolio_view held rows.",
        },
        {
          key: "COUNT / COUNT",
          label: "Quotes & greeks",
          why: "How many lines cannot be marked from a real two-sided market.",
          how: "Counts rows missing a bid or an ask, and rows whose risk_source is 'missing'.",
          subject: "book",
          timeBasis: "snapshot",
          role: "situational",
          whatItIs: "Two counts, deliberately not a coverage percentage.",
          limits:
            "There is NO 'quote coverage %' on this page on purpose. The backend's Mid COALESCE chain terminates at the stored mark, so a coverage percentage built on 'has a mid' would read ~100% on a book with no live quotes at all. Open interest is also absent: it COALESCEs through dbo.OptionPrices, dead since 2025.",
          source: "/get_portfolio_view raw rows, before the display mapper drops these fields.",
        },
        {
          key: "COUNT",
          label: "Cost basis on file",
          why: "Lines with no entry price recorded.",
          how: "Counts rows where entry_price is null or zero, and sums their market value.",
          subject: "book",
          timeBasis: "snapshot",
          role: "drives",
          whatItIs: "Positions whose cost is unknown.",
          tellsYou:
            "Worth watching precisely because it usually reads zero. A null entry is coerced to $0 cost in both cost paths, which UNDERSTATES cost basis and therefore understates the funded-basis percentage measured against the 40% cap — an error in the reassuring direction.",
          readWith: "Sizing & rebalance → funded basis.",
          source: "/get_portfolio_view held rows.",
        },
        {
          key: "USD",
          label: "Harvest lockout",
          why: "Capital that is not deployable.",
          how: "homeRun.summary, gated on homeRun.policy.enabled actually being a boolean.",
          subject: "book",
          timeBasis: "snapshot",
          role: "drives",
          whatItIs: "Harvested proceeds under a 60-calendar-day redeployment lock.",
          limits:
            "Reads 'Not loaded' rather than '$0' when the policy block is absent. The backend falls back to an empty policy with lockedNotional 0 on any exception inside the harvest builder, so an unguarded read would render a confident 'no lockout' when the truth is 'we do not know'.",
          readWith: "Signals, which owns the harvest decision and the Record TWS Trim action.",
          source: "strategy/home_run_redeployment.py via /get_portfolio_view.",
        },
        {
          key: "COUNT",
          label: "Payload build",
          why: "Whether the backend hit a problem assembling this page's data.",
          how: "The warnings[] array get_portfolio_view already emits.",
          subject: "book",
          timeBasis: "live",
          role: "situational",
          whatItIs: "Per-stage build warnings — overview, held_leaps, fixed_income, home_run, rebalance.",
          tellsYou:
            "That a section of the payload degraded. Notably rebalance:fallback_used, which means the sizing figures came from the market-based fallback rather than the engine.",
          limits: "The backend has always emitted these; nothing read them until 2026-08-27.",
          source: "/get_portfolio_view warnings[].",
        },
      ],
    },
    {
      name: "Positions",
      summary: "The exit review queue and the full holdings ledger — the only per-line cost basis and liquidity read in the app.",
      metrics: [
        {
          key: "RANK",
          label: "Exit rank",
          why: "This line's PLACE in today's book on the combined rule + model exit signals.",
          how: "Rendered as an ordinal with its denominator: '2 of 14'.",
          subject: "book",
          timeBasis: "snapshot",
          role: "situational",
          whatItIs: "An ORDER, not a level.",
          readIt:
            "Somebody is always 1st, even when the whole book is calm and every underlying score is near zero. A high rank with a low score means 'most exit-worthy of a quiet book', not 'urgent'.",
          limits:
            "Carries no information about whether anything is actually wrong. Read the Exit score beside it for that. As of 2026-07-31 the rank drives no posture escalation, because a fixed number of lines topped it every day regardless of risk.",
          readWith: "Exit score, posture, and the named drivers.",
          source: "exit_hints via /get_portfolio_view.",
        },
        {
          key: "0–100",
          label: "Exit score",
          why: "The absolute exit signal for a line.",
          how: "Rule + model blend.",
          subject: "book",
          timeBasis: "snapshot",
          role: "situational",
          whatItIs: "A level, distinct from the within-book rank.",
          limits:
            "Validation (HelionBacktest/exit_ml_validation) found the raw score saturated, regime-dependent and miscalibrated, mostly restating drawdown and DTE, and barely beating a simple drawdown+DTE rule outside stress. Anchor decisions on posture and legible drivers, not on this number.",
          source: "exit_hints.",
        },
        {
          key: "STATE",
          label: "Hard exit",
          why: "A hard exit condition on the line.",
          how: "dbo.HeldLeaps.hard_sell.",
          subject: "book",
          timeBasis: "snapshot",
          role: "situational",
          whatItIs: "A flag that, when present, names its reason.",
          limits:
            "NO PRODUCER WRITES THIS FIELD. The absence of a hard-exit badge means the check DOES NOT RUN — not that the position was checked and found clear. Never read a clean queue as an all-clear on this dimension.",
          source: "dbo.HeldLeaps.hard_sell — currently unwritten.",
        },
        {
          key: "USD / %",
          label: "Entry, market, return",
          why: "What a line cost and what it is worth.",
          how: "return_pct is a persisted computed column: market_price / entry_price − 1.",
          subject: "book",
          timeBasis: "snapshot",
          role: "informational",
          whatItIs: "The only per-line cost basis available anywhere in the app for an unflagged position.",
          limits:
            "market_price is a MARK, whatever the quote feed last wrote — it has been the bid, and as of 2026-08-27 it is the mid. A basis change steps every return up or down at once, and shallower drawdowns after such a change are a basis transition, not a display fault. Peaks recorded under one basis and compared to marks under another understate drawdown until every peak is re-recorded.",
          readWith: "Audit checks → Greeks snapshot, and → Quotes & greeks.",
          source: "dbo.HeldLeaps via /get_portfolio_view.",
        },
      ],
    },
    {
      name: "Runway",
      summary: "How much of the book is running out of time.",
      metrics: [
        {
          key: "DTE BUCKETS",
          label: "Expiry runway",
          why: "Calendar time remaining across the option sleeve.",
          how: "Six DTE buckets; only buckets holding something are rendered.",
          subject: "book",
          timeBasis: "snapshot",
          role: "informational",
          whatItIs: "Market value and line count by days-to-expiry.",
          limits:
            "This is NOT the surface deployment ladder on Risk (<18m / 18–24m / 24–30m / 30–36m), which is what the allocator reasons about. Different boundaries, on purpose. Do not compare the two bucket sets.",
          readWith: "Risk → the deployment ladder.",
          source: "/get_portfolio_view held rows.",
        },
      ],
    },
    {
      name: "Sizing & rebalance",
      summary:
        "Kept here for reference only. Signals is authoritative for sizing; this drawer exists so you do not have to hold two pages in your head to answer one question.",
      metrics: [
        {
          key: "% OF BROKER BOOK",
          label: "Options sleeve target vs current",
          why: "Where the sleeve sits against its regime target.",
          how: "rebalance.current_weight against target_pct_effective, both against the BROKER BOOK.",
          subject: "book",
          timeBasis: "close",
          role: "drives",
          whatItIs: "Market-value weight versus the regime target.",
          limits:
            "A different denominator from the NAV hero above. Market value above target is not, by itself, a trim signal.",
          readWith: "The NAV hero composition, which uses the reconciled total.",
          source: "/get_portfolio_view rebalance block.",
        },
        {
          key: "% OF BROKER BOOK",
          label: "Funded basis vs the 40% cap",
          why: "How much cost basis is deployed against the deployment cap.",
          how: "Cost basis of held LEAPS over the broker book.",
          subject: "book",
          timeBasis: "close",
          role: "drives",
          whatItIs: "The cap basis. THE 40% IS A PURCHASE CONSTRAINT, NOT A HOLDING CAP.",
          readIt:
            "At or above 40% means no further funded buying. Market value may grow without limit — there is no cap on options performance.",
          limits:
            "Reaching the cap is NOT a trim trigger. Only a regime change or the grind axis moves the allocation down (target_source: base_regime, early_defense, grind_defense). A missing entry price counts as $0 cost, which biases this figure UNDER the cap.",
          readWith: "Audit checks → Cost basis on file. Signals, which is authoritative.",
          source: "strategy/rebalance_logic.py compute_funded_cap_trim.",
        },
        {
          key: "USD",
          label: "Trim preview and sell preview",
          why: "What a trim would actually touch.",
          how: "suggested_trim_to_target / hard_cap_trim, and the engine's per-contract order preview.",
          subject: "forward",
          timeBasis: "close",
          role: "situational",
          whatItIs:
            "The only place in the app that shows WHICH specific contracts a trim would touch before anything is staged.",
          limits:
            "Also the only surface that admits the rebalance engine FAILED TO EVALUATE, rather than silently showing 'no trim'. If it says the engine did not run, that is not the same as 'nothing to do'.",
          source: "/get_portfolio_view rebalance + rebalanceOrdersPreview.",
        },
      ],
    },
  ],

  playbook: [
    {
      title: "Open the page, read four things",
      detail:
        "Total NAV and whether its parts tie; the period return beneath it; the audit strip (can I trust this?); and Owed action today (does anything need me?). If the owed panel is clear and the audit strip passes, you are done — the drawers are depth, not homework.",
    },
    {
      title: "Never compare a hero % to a sizing %",
      detail:
        "The hero divides by reconciled NAV; sizing divides by the broker book. The same sleeve reads differently in the two places by design. Each is labelled at the point of use.",
    },
    {
      title: "A funded-cap breach means stop buying, not sell",
      detail:
        "The 40% governs what we BUY on cost basis, not what we HOLD. Only a regime change or the grind axis forces a rebalance down. If a page ever tells you to trim on a cap breach alone, that page is wrong.",
    },
    {
      title: "Treat 'unknown' as a finding",
      detail:
        "Audit checks fail toward warning, never toward green. 'Not run', 'Unknown' and 'Not loaded' each mean a check could not be completed — they are not clean bills of health, and the figures they cover should not be quoted until they resolve.",
    },
    {
      title: "Confirm the quote before harvesting",
      detail:
        "Harvest multiples are struck on the book mark, not on an executable price. A line at 2.0x on the mark can be under 2x at the bid, and recording a trim starts a 60-calendar-day lockout.",
    },
  ],

  earlyReads: [
    {
      title: "'NAV basis: Broker only' with a normal-looking NAV",
      detail:
        "The administrator overlay did not load and the page fell back to the IBKR book. The headline is still plausible, which is exactly why this line exists. Do not quote NAV until it resolves.",
    },
    {
      title: "Greeks snapshot older than the headline freshness chip",
      detail:
        "Expected — the chip shows the freshest line, the audit check shows the oldest. A wide gap means part of the book has not been re-quoted while part has.",
    },
    {
      title: "Cost basis on file goes non-zero",
      detail:
        "Funded basis is now understated against the 40% cap, because a null entry price counts as $0 cost. The cap will read further away than it is.",
    },
    {
      title: "Staged tickets reads '50+'",
      detail:
        "The payload is capped at 50 and the true figure is unknown. Go to the Trade Desk, whose query is unlimited.",
    },
    {
      title: "Drawdowns suddenly read shallower across the book",
      detail:
        "Check whether the mark basis moved (bid to mid, or back). Peaks recorded under the old basis compared against marks under the new one understate drawdown until the peaks re-record. This is a basis transition, not a display fault.",
    },
  ],

  operations: {
    dataSources: [
      {
        label: "/get_portfolio_view",
        detail:
          "Holdings, rebalance meta, staged orders (SELECT TOP 50), home-run harvest, fixed income. Also emits warnings[], surfaced in the audit strip.",
      },
      {
        label: "/risk/nav_latest",
        detail:
          "Reconciled NAV, the daily overlay legs and the administrator cross-check. Falls back to /get_portfolio_nav_latest, which carries NO reconciliation block — the fallback is what 'NAV basis: Broker only' detects.",
      },
      {
        label: "/risk/leaps_metrics",
        detail:
          "Greeks and quote hydration for rows missing them. Reads SQL (v_HeldLeaps_Display, IbkrPositionsRaw), not the live IBKR socket.",
      },
      {
        label: "/refresh_portfolio",
        detail:
          "The Refresh holdings button. Refreshes holdings from IBKR only — it does NOT write the official daily NAV (dbo.PortfolioNAV). That is written by the scheduled jobs (06:15 refresh, 06:45 and 13:45 MT risk preflight), and the 14:05 MT close mark is final; a correction after that is a supervised, CIO-requested action. Returns HTTP 200 even when the IBKR sync FAILS, degrading to last-known data — so success is read from the explicit syncOk / sync.error fields, not the status code. navOk = false means the broker NAV couldn't be read (the stored daily NAV is unchanged).",
      },
    ],
    freshness: [
      {
        label: "One load wave",
        detail:
          "The three loads start together and the page paints once, so no block can show a tie-out before its own reconciliation has resolved.",
      },
      {
        label: "Trading sessions, not hours",
        detail:
          "Staleness is measured in US trading sessions (DST-correct, weekend-safe). Friday's data read on Monday is 1 session behind, not stale. Grace is 2 sessions.",
      },
      {
        label: "Holidays are not modelled",
        detail: "The 2-session grace absorbs the occasional holiday — a deliberate choice to under-alarm rather than cry wolf.",
      },
    ],
    governance: [
      {
        label: "Advisory only",
        detail:
          "Nothing on this page transmits an order. All execution is manual, in IBKR TWS.",
      },
      {
        label: "Harvest recording (on Signals)",
        detail:
          "Recording a harvest is done on the SIGNALS page, not here. There is no backend timer behind the lockout — it is started only by a human recording an execution, and there are now TWO such paths: the Record TWS Trim control on Signals, and marking a `home_run_trim` ticket EXECUTED on the Trade Desk (wired 2026-09-02). The Trade Desk path judges the FILL, not the ticket: a disposition that takes the whole line is a full exit and deliberately starts NO lockout (P1-FULLEXIT, 2026-09-04).",
      },
      {
        label: "One execution, two records",
        detail:
          "Recording a harvest writes the lockout but NOT the trade ledger, so the same IBKR fill also appears in the Trade Desk break queue asking to be claimed. That is a known seam, logged rather than papered over.",
      },
      {
        label: "Disposition moved",
        detail:
          "Executed / Cancelled now live on the Trade Desk, which captures fill quantity and average price. This page's old two-button path silently dropped them.",
      },
    ],
    governanceNote:
      "Nothing here is investor-facing output, legal, tax or compliance-approved language. The strategy is volatility-aware and convex — not safe, guaranteed, bond-like or low risk — and every position can lose principal. Where a figure is estimated, carried from a prior strike, capped, or struck on a mark rather than an executable price, this page says so at the point of use.",
  },
};

export default PORTFOLIO_METHODOLOGY;
